import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Badge, Row, Col, Form, Spinner } from 'react-bootstrap';
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getFilteredRowModel,
    flexRender,
} from '@tanstack/react-table';
import { toast } from 'react-toastify';
import './SupplyInventory.css';
import { displayDate, displayOutDate } from '../../utils/date';
import { getVisiblePages } from '../../utils/pagination';
import { deleteSupply, getSupplies } from '../../services/Supplies/Supplies';
import { getUnitMeasures } from '../../services/Other/GeneralQueries';
import ConfirmModal from '../../components/common/ConfirmModal';
import AddSuppliesModal from '../../pages/Supplies/AddSupplies';
import EditSuppliesModal from './EditSupplies';

function SupplyInventory() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [showAdd, setShowAdd] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showEdit, setShowEdit] = useState(false);
    const [editId, setEditId] = useState(null);
    const [confirm, setConfirm] = useState({
        show: false,
        id: null,
        isLoading: false,
    });

    const [dateField, setDateField] = useState('acquisitionDate'); // o 'outDate'
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const fetchRows = async () => {
        setLoading(true);
        try {
            const res = await getSupplies();
            const um = await getUnitMeasures();
            const umMap = {};
            (um ?? []).forEach((u) => {
                umMap[u.id] = `${u.name} (${u.abbreviation})`;
            });

            if (!res) {
                toast.error('No se pudieron cargar los consumibles.');
                setData([]);
                return;
            }

            if (!um) {
                toast.error('No se pudieron cargar las unidades de medida. Se mostrarán como "N/A".');
                setData([]);
                return;
            }

            setData(
                res.map((r) => ({
                    ...r,
                    unitofmeasure_label: umMap[r.unitofmeasure_id] ?? 'N/A',
                }))
            );
        } catch (e) {
            toast.error('Error cargando consumibles.');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRows();
    }, []);

    const filteredData = useMemo(() => {
        const from = dateFrom || null; // "YYYY-MM-DD"
        const to = dateTo || null;

        return (data || []).filter(r => {
            // status
            if (statusFilter === 'active' && !r.isActive) return false;
            if (statusFilter === 'inactive' && r.isActive) return false;

            // dates
            if (!from && !to) return true;

            const v = String(r?.[dateField] || '').slice(0, 10); // "YYYY-MM-DD"
            if (!v) return false;

            if (from && v < from) return false;
            if (to && v > to) return false;

            return true;
        });
    }, [data, statusFilter, dateField, dateFrom, dateTo]);

    const handleOpenEdit = (id) => {
        setEditId(id);
        setShowEdit(true);
    };

    const handleAdd = () => setShowAdd(true);

    const handleSaved = () => {
        setShowAdd(false);
        fetchRows();
    };

    const askDelete = (id) => setConfirm({ show: true, id, isLoading: false });

    const confirmDelete = async () => {
        if (!confirm.id) return;
        setConfirm((p) => ({ ...p, isLoading: true }));
        try {
            const res = await deleteSupply(confirm.id);
            if (!res?.result) {
                toast.error(res?.message ?? 'No se pudo eliminar.');
                return;
            }
            toast.success('Consumible eliminado.');
            setConfirm({ show: false, id: null, isLoading: false });
            fetchRows();
        } catch (e) {
            toast.error('Error eliminando consumible.');
        } finally {
            setConfirm((p) => ({ ...p, isLoading: false }));
        }
    };

    // Filtrado por Estado (si no existe isActive, lo tomamos como true)
    const filteredByStatus = useMemo(() => {
        if (statusFilter === 'all') return data;

        const wantActive = statusFilter === 'active';
        return data.filter((r) => {
            const active = r?.isActive ?? true;
            return wantActive ? active : !active;
        });
    }, [data, statusFilter]);

    const money = (value) => {
        const n = Number(value ?? 0);
        return isNaN(n)
            ? value
            : new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);
    };

    const num = (value) => {
        const n = Number(value ?? 0);
        return isNaN(n) ? value : new Intl.NumberFormat('es-MX', { maximumFractionDigits: 4 }).format(n);
    };

    const columns = useMemo(
        () => [
            {
                id: 'actions',
                header: 'Acciones',
                cell: ({ row }) => (
                    <div className="actions-cell">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleOpenEdit(row.original.id)}
                        >
                            Editar
                        </Button>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => askDelete(row.original.id)}
                        >
                            Eliminar
                        </Button>
                    </div>
                ),
                meta: { thClass: 'actions-col', tdClass: 'actions-col' },
            },
            { header: 'Clave', accessorKey: 'code', meta: { thClass: 'col-code', tdClass: 'col-code' } },
            { header: 'Nombre', accessorKey: 'product_name', meta: { thClass: 'col-name', tdClass: 'col-name' } },
            {
                header: 'Fecha entrada',
                accessorKey: 'acquisitionDate',
                cell: ({ getValue }) => displayDate(getValue()),
            },
            {
                header: 'Fecha salida',
                accessorKey: 'outDate',
                cell: ({ getValue }) => displayOutDate(getValue()),
            },
            {
                header: 'Lote',
                accessorKey: 'lot',
                cell: ({ getValue }) => num(getValue()),
            },
            {
                header: 'Piezas/lote',
                accessorKey: 'pieces_per_lot',
                cell: ({ getValue }) => num(getValue()),
            },
            {
                header: 'Costo/lote',
                accessorKey: 'lot_cost',
                cell: ({ getValue }) => money(getValue()),
            },
            {
                header: 'Unidad de Medida',
                accessorKey: 'unitofmeasure_label',
                meta: { thClass: 'col-nowrap', tdClass: 'col-nowrap' }
            },
            { header: 'Capacidad', accessorKey: 'capacity_dimensions' },
            { header: 'Proveedor', accessorKey: 'supplier', meta: { thClass: 'col-supplier', tdClass: 'col-supplier' } },
            {
                header: 'Costo/unidad',
                accessorKey: 'unit_cost',
                cell: ({ getValue }) => money(getValue()),
            },
            {
                header: 'Precio/ml',
                accessorKey: 'price_per_ml',
                cell: ({ getValue }) => money(getValue()),
            },
            {
                header: 'Estado',
                id: 'status',
                cell: ({ row }) => {
                    const active = row.original?.isActive ?? true;
                    return (
                        <Badge bg={active ? 'success' : 'secondary'}>
                            {active ? 'Activa' : 'Inactiva'}
                        </Badge>
                    );
                },
            },
        ],
        []
    );

    const table = useReactTable({
        data: filteredData,
        columns,
        state: {
            globalFilter,
        },
        onGlobalFilterChange: setGlobalFilter,

        globalFilterFn: (row, _columnId, filterValue) => {
            const q = String(filterValue ?? '').toLowerCase().trim();
            if (!q) return true;

            // Busca en “valores visibles” de varias columnas
            const r = row.original ?? {};
            const haystack = [
                r.code,
                r.product_name,
                r.unit_of_measure,
                r.capacity_dimensions,
                r.supplier,
                r.lot,
                r.pieces_per_lot,
                r.lot_cost,
                r.unit_cost,
                r.price_per_ml,
            ]
                .map((x) => String(x ?? '').toLowerCase())
                .join(' ');

            return haystack.includes(q);
        },

        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const totalRows = table.getFilteredRowModel().rows.length;
    const pageIndex = table.getState().pagination.pageIndex;
    const pageSize = table.getState().pagination.pageSize;
    const pageCount = table.getPageCount();
    const currentPage = pageCount === 0 ? 0 : pageIndex + 1;
    const visiblePages = getVisiblePages(currentPage, pageCount);

    return (
        <>
            {/* Header / breadcrumb (si ya lo manejas en otro layout, puedes omitirlo) */}
            <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                    <h2 className="mb-0">Consumibles</h2>
                </div>

                <Button variant="primary" onClick={handleAdd}>
                    Agregar
                </Button>
            </div>

            {/* Filtros */}
            <Row className="g-3 align-items-end mb-3">
                {/* ESTADO */}
                <Col md={3}>
                    <Form.Label>Estado</Form.Label>
                    <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="all">Todos</option>
                        <option value="active">Activos</option>
                        <option value="inactive">Inactivos</option>
                    </Form.Select>
                </Col>
                {/* BUSCAR */}
                <Col md={6}>
                    <Form.Label>Buscar</Form.Label>
                    <Form.Control
                        value={globalFilter ?? ''}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder="Buscar por clave, nombre, proveedor..."
                    />
                </Col>
                {/* POR PAGINA */}
                <Col md={3}>
                    <Form.Label>Por página</Form.Label>
                    <Form.Select
                        value={pageSize}
                        onChange={(e) => table.setPageSize(Number(e.target.value))}
                    >
                        {[10, 20, 30, 50].map((n) => (
                            <option key={n} value={n}>
                                {n}
                            </option>
                        ))}
                    </Form.Select>
                </Col>
                {/* FECHA */}
                <Row className="g-3 align-items-end mb-3">
                    <Col md={3}>
                        <Form.Label>Filtrar por</Form.Label>
                        <Form.Select value={dateField} onChange={(e) => setDateField(e.target.value)}>
                            <option value="acquisitionDate">Fecha entrada</option>
                            <option value="outDate">Fecha salida</option>
                        </Form.Select>
                    </Col>

                    <Col md={3}>
                        <Form.Label>Desde</Form.Label>
                        <Form.Control
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                        />
                    </Col>

                    <Col md={3}>
                        <Form.Label>Hasta</Form.Label>
                        <Form.Control
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                        />
                    </Col>

                    <Col md={3} className="d-flex gap-2">
                        <Button
                            variant="outline-secondary"
                            className="mt-4"
                            onClick={() => { setDateFrom(''); setDateTo(''); setDateField('acquisitionDate'); }}
                        >
                            Limpiar fecha
                        </Button>
                    </Col>
                </Row>
            </Row>

            {/* Tabla */}
            <div className="table-rounded-wrapper">
                <div className="table-responsive">
                    <Table bordered hover className="mb-0 align-middle">
                        <thead>
                            {table.getHeaderGroups().map((hg) => (
                                <tr key={hg.id}>
                                    {hg.headers.map((header) => {
                                        const thClass = header.column.columnDef.meta?.thClass ?? '';
                                        return (
                                            <th key={header.id} className={thClass}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))}
                        </thead>

                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={columns.length} className="text-center py-5">
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Cargando...
                                    </td>
                                </tr>
                            ) : table.getRowModel().rows.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length} className="text-center py-5">
                                        Sin registros
                                    </td>
                                </tr>
                            ) : (
                                table.getRowModel().rows.map((row) => (
                                    <tr key={row.id}>
                                        {row.getVisibleCells().map((cell) => {
                                            const tdClass = cell.column.columnDef.meta?.tdClass ?? '';
                                            return (
                                                <td key={cell.id} className={tdClass}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>

            {/* Footer / paginación */}
            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                <div className="text-muted">
                    Página {currentPage} de {pageCount} ({totalRows} registros)
                </div>

                <div className="d-flex gap-2 align-items-center flex-wrap">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                    >
                        «
                    </Button>

                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        Anterior
                    </Button>

                    {visiblePages.length > 0 && visiblePages[0] > 1 && (
                        <>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => table.setPageIndex(0)}
                            >
                                1
                            </Button>
                            {visiblePages[0] > 2 && <span className="px-1 text-muted">…</span>}
                        </>
                    )}

                    {visiblePages.map((pageNumber) => (
                        <Button
                            key={pageNumber}
                            size="sm"
                            variant={pageNumber === currentPage ? 'secondary' : 'outline-secondary'}
                            onClick={() => table.setPageIndex(pageNumber - 1)}
                        >
                            {pageNumber}
                        </Button>
                    ))}

                    {visiblePages.length > 0 && visiblePages[visiblePages.length - 1] < pageCount && (
                        <>
                            {visiblePages[visiblePages.length - 1] < pageCount - 1 && (
                                <span className="px-1 text-muted">…</span>
                            )}
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => table.setPageIndex(pageCount - 1)}
                            >
                                {pageCount}
                            </Button>
                        </>
                    )}

                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        Siguiente
                    </Button>

                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => table.setPageIndex(pageCount - 1)}
                        disabled={!table.getCanNextPage()}
                    >
                        »
                    </Button>
                </div>
            </div>

            {/* Confirmación eliminar */}
            <ConfirmModal
                show={confirm.show}
                onHide={() => setConfirm({ show: false, id: null, isLoading: false })}
                onConfirm={confirmDelete}
                title="Eliminar consumible"
                message="¿Seguro que deseas eliminar este consumible?"
                confirmText="Eliminar"
                confirmVariant="danger"
                denyText={null}
                isLoading={confirm.isLoading}
            />

            <AddSuppliesModal
                show={showAdd}
                onHide={() => setShowAdd(false)}
                onSaved={handleSaved}
            />

            <EditSuppliesModal
                show={showEdit}
                supplyId={editId}
                onHide={() => { setShowEdit(false); setEditId(null); }}
                onSaved={() => { setShowEdit(false); setEditId(null); fetchRows(); }}
            />
        </>
    );
}

export default SupplyInventory;